/**
 * Format a YYYY-MM-DD date string to a readable localized format
 * e.g. "20 maart 2026" (NL), "20 marca 2026" (PL), "20 Mart 2026" (TR)
 */
import { isSchoolHoliday } from './schoolHolidays';

const MONTHS = {
  nl: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  pl: ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'],
};

export function formatDateNL(dateStr, lang = 'nl') {
  if (!dateStr) return '-';
  try {
    const months = MONTHS[lang] || MONTHS['nl'];
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const day = parseInt(parts[2], 10);
      const month = months[parseInt(parts[1], 10) - 1];
      const year = parts[0];
      return `${day} ${month} ${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

/**
 * Format recurring days array to a readable localized string
 * e.g. ["ma", "wo", "vr"] → "Ma · Wo · Vr" (NL), "Pzt · Çar · Cum" (TR)
 */
const DAY_FULL = {
  nl: { ma: 'maandag', di: 'dinsdag', wo: 'woensdag', do: 'donderdag', vr: 'vrijdag', za: 'zaterdag', zo: 'zondag' },
  en: { ma: 'Monday', di: 'Tuesday', wo: 'Wednesday', do: 'Thursday', vr: 'Friday', za: 'Saturday', zo: 'Sunday' },
  tr: { ma: 'Pazartesi', di: 'Salı', wo: 'Çarşamba', do: 'Perşembe', vr: 'Cuma', za: 'Cumartesi', zo: 'Pazar' },
  pl: { ma: 'poniedziałek', di: 'wtorek', wo: 'środa', do: 'czwartek', vr: 'piątek', za: 'sobota', zo: 'niedziela' },
};
const DAY_SHORT = {
  nl: { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' },
  en: { ma: 'Mon', di: 'Tue', wo: 'Wed', do: 'Thu', vr: 'Fri', za: 'Sat', zo: 'Sun' },
  tr: { ma: 'Pzt', di: 'Sal', wo: 'Çar', do: 'Per', vr: 'Cum', za: 'Cmt', zo: 'Paz' },
  pl: { ma: 'Pon', di: 'Wt', wo: 'Śr', do: 'Czw', vr: 'Pt', za: 'Sob', zo: 'Nd' },
};
const LABELS = {
  nl: { daily: 'Dagelijks', weekdays: 'Doordeweeks', every: 'Elke' },
  en: { daily: 'Daily', weekdays: 'Weekdays', every: 'Every' },
  tr: { daily: 'Her gün', weekdays: 'Hafta içi', every: 'Her' },
  pl: { daily: 'Codziennie', weekdays: 'Dni robocze', every: 'Co' },
};
const DAY_ORDER = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr'];
// Normalize day code variants (EN, NL legacy) to canonical NL short codes used everywhere else.
const DAY_ALIAS = {
  mo: 'ma', mon: 'ma', monday: 'ma',
  tu: 'di', tue: 'di', tuesday: 'di',
  we: 'wo', wed: 'wo', wednesday: 'wo',
  th: 'do', thu: 'do', thursday: 'do',
  fr: 'vr', fri: 'vr', friday: 'vr',
  sa: 'za', sat: 'za', saturday: 'za',
  su: 'zo', sun: 'zo', sunday: 'zo',
};
function normaliseDay(code) {
  if (!code) return code;
  const c = String(code).toLowerCase();
  if (DAY_ORDER.includes(c)) return c;
  return DAY_ALIAS[c] || c;
}
// JS Date#getDay → 0..6 (Sun..Sat) → canonical short codes (ma..zo).
const JS_DAY_TO_CODE = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

export function formatRecurringDays(days, lang = 'nl') {
  if (!days || !Array.isArray(days) || days.length === 0) return null;
  const normalised = days.map(normaliseDay).filter((d) => DAY_ORDER.includes(d));
  if (normalised.length === 0) return null;
  const sorted = [...new Set(normalised)].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const labels = LABELS[lang] || LABELS['nl'];
  const dayFull = DAY_FULL[lang] || DAY_FULL['nl'];
  const dayShort = DAY_SHORT[lang] || DAY_SHORT['nl'];

  if (sorted.length === 7) return labels.daily;
  if (sorted.length === 5 && WEEKDAYS.every(d => sorted.includes(d))) return labels.weekdays;
  if (sorted.length === 1) return `${labels.every} ${dayFull[sorted[0]] || sorted[0]}`;
  return sorted.map(d => dayShort[d] || d).join(' · ');
}

/**
 * Return the human-readable date label for an activity, handling recurring patterns.
 * - One-off: the stored date (e.g. "20 maart 2026")
 * - Daily recurring: "Dagelijks"
 * - Pattern with specific days: "Ma · Wo · Vr" / "Doordeweeks" / "Elke maandag"
 * - Weekly without days: derive weekday from activity.date → "Elke woensdag"
 * - Biweekly without days: "Om de week op woensdag"
 * - Monthly: "Maandelijks op de 28e" derived from activity.date
 */
const PATTERN_LABELS = {
  nl: { daily: 'Dagelijks', weekdays: 'Doordeweeks', weekly: 'Wekelijks', biweekly: 'Tweewekelijks', monthly: 'Maandelijks', ongoing: 'Doorlopend' },
  en: { daily: 'Daily', weekdays: 'Weekdays', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', ongoing: 'Ongoing' },
  tr: { daily: 'Her gün', weekdays: 'Hafta içi', weekly: 'Haftalık', biweekly: 'İki haftada bir', monthly: 'Aylık', ongoing: 'Süregelen' },
  pl: { daily: 'Codziennie', weekdays: 'Dni robocze', weekly: 'Co tydzień', biweekly: 'Co dwa tygodnie', monthly: 'Co miesiąc', ongoing: 'Ciągły' },
};
const BIWEEKLY_PREFIX = {
  nl: 'Om de week op',
  en: 'Every other',
  tr: 'İki haftada bir',
  pl: 'Co dwa tygodnie w',
};
const MONTHLY_ON_DAY = {
  nl: (n) => `Maandelijks op de ${n}e`,
  en: (n) => `Monthly on the ${n}${ordinalSuffix(n)}`,
  tr: (n) => `Aylık olarak ${n}. günü`,
  pl: (n) => `Miesięcznie ${n}.`,
};
function ordinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const NTH_WEEKDAY = {
  nl: { 1: '1e', 2: '2e', 3: '3e', 4: '4e', 5: 'laatste' },
  en: { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: 'last' },
  tr: { 1: '1.', 2: '2.', 3: '3.', 4: '4.', 5: 'son' },
  pl: { 1: '1.', 2: '2.', 3: '3.', 4: '4.', 5: 'ostatni' },
};

/**
 * Try to infer a human-readable cadence from an `upcoming_dates` array.
 *
 * Recognises:
 *   • Monthly on the Nth weekday  ("4e woensdag van de maand")
 *   • Monthly on a fixed day-of-month ("Maandelijks op de 27e")
 *   • Weekly on a single weekday ("Elke woensdag")
 *
 * Returns `null` when no clean pattern can be derived — caller should then
 * fall back to its existing logic.
 */
function inferCadenceFromUpcoming(dates, lang = 'nl') {
  if (!dates || !Array.isArray(dates) || dates.length < 2) return null;
  const parsed = dates
    .map(parseISODateLocal)
    .filter(Boolean)
    .sort((a, b) => a - b);
  if (parsed.length < 2) return null;

  const labels = LABELS[lang] || LABELS['nl'];
  const dayFull = DAY_FULL[lang] || DAY_FULL['nl'];
  const ntable = NTH_WEEKDAY[lang] || NTH_WEEKDAY.nl;

  // All same weekday?
  const weekdays = new Set(parsed.map((d) => d.getDay()));
  const sameWeekday = weekdays.size === 1;
  // Gaps in days between consecutive dates
  const gaps = parsed.slice(1).map((d, i) => Math.round((d - parsed[i]) / 86400000));
  const allGapsMonthly = gaps.every((g) => g >= 26 && g <= 35);
  const allGapsWeekly = gaps.every((g) => g === 7);

  if (sameWeekday && allGapsMonthly) {
    // Nth weekday of the month: compute N for the first date
    const d = parsed[0];
    const nth = Math.floor((d.getDate() - 1) / 7) + 1;
    const code = JS_DAY_TO_CODE[d.getDay()];
    const nthLabel = ntable[nth] || `${nth}e`;
    const wd = dayFull[code] || code;
    // "elke 4e woensdag van de maand" / "every 4th Wednesday of the month"
    if (lang === 'en') return `Every ${nthLabel} ${wd} of the month`;
    return `Elke ${nthLabel} ${wd} van de maand`;
  }
  if (allGapsMonthly) {
    // Monthly but not always same weekday → "Maandelijks op de N(e)" if same dayOfMonth
    const doms = new Set(parsed.map((d) => d.getDate()));
    if (doms.size === 1) {
      const n = parsed[0].getDate();
      const fn = MONTHLY_ON_DAY[lang] || MONTHLY_ON_DAY.nl;
      return fn(n);
    }
    return (PATTERN_LABELS[lang] || PATTERN_LABELS.nl).monthly;
  }
  if (sameWeekday && allGapsWeekly) {
    const code = JS_DAY_TO_CODE[parsed[0].getDay()];
    return `${labels.every} ${dayFull[code] || code}`;
  }
  return null;
}

function parseISODateLocal(dateStr) {
  if (!dateStr) return null;
  const parts = String(dateStr).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  return new Date(y, m - 1, d);
}

export function getActivityDateLabel(activity, lang = 'nl') {
  if (!activity) return '-';
  const isRecurring = activity.recurring || activity.recurring_pattern || (activity.recurring_days && activity.recurring_days.length > 0);
  const patternLabels = PATTERN_LABELS[lang] || PATTERN_LABELS['nl'];

  if (isRecurring) {
    // Explicit "Nde weekdag van de maand" — bouw label uit opgeslagen velden
    // zodat er geen upcoming_dates nodig zijn om "Elke 2e woensdag van de
    // maand" weer te geven.
    if (activity.recurring_pattern === 'monthly_weekday'
        && activity.recurring_week_ordinal != null
        && activity.recurring_weekday != null) {
      const dayFull = DAY_FULL[lang] || DAY_FULL['nl'];
      const ntable = NTH_WEEKDAY[lang] || NTH_WEEKDAY.nl;
      const ord = activity.recurring_week_ordinal === -1 ? 5 : activity.recurring_week_ordinal;
      const nthLabel = ntable[ord] || `${ord}e`;
      const code = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'][activity.recurring_weekday];
      const wd = dayFull[code] || code;
      if (lang === 'en') return `Every ${nthLabel} ${wd} of the month`;
      return `Elke ${nthLabel} ${wd} van de maand`;
    }

    // 1. If we have an explicit upcoming_dates list, derive the cadence from it.
    //    Catches "4e woensdag van de maand" and other patterns that don't fit
    //    in the simple `recurring_pattern` enum.
    const fromUpcoming = inferCadenceFromUpcoming(activity.upcoming_dates, lang);
    if (fromUpcoming) return fromUpcoming;

    // 2. Days picked explicitly → use weekday formatter (handles "Doordeweeks", "Elke maandag", etc.)
    if (activity.recurring_days && activity.recurring_days.length > 0) {
      const days = formatRecurringDays(activity.recurring_days, lang);
      if (days) return days;
    }

    const pattern = activity.recurring_pattern;
    const dayFull = DAY_FULL[lang] || DAY_FULL['nl'];
    const labels = LABELS[lang] || LABELS['nl'];
    const baseDate = parseISODateLocal(activity.date);

    // 2. Pattern-specific intelligent fallbacks using base date weekday/dayOfMonth.
    if (pattern === 'daily') return patternLabels.daily;
    if (pattern === 'weekdays') return patternLabels.weekdays;
    if ((pattern === 'weekly' || !pattern) && baseDate) {
      const dayCode = JS_DAY_TO_CODE[baseDate.getDay()];
      return `${labels.every} ${dayFull[dayCode] || dayCode}`;
    }
    if (pattern === 'biweekly' && baseDate) {
      const dayCode = JS_DAY_TO_CODE[baseDate.getDay()];
      return `${BIWEEKLY_PREFIX[lang] || BIWEEKLY_PREFIX.nl} ${dayFull[dayCode] || dayCode}`;
    }
    if (pattern === 'monthly' && baseDate) {
      const dayOfMonth = baseDate.getDate();
      const fn = MONTHLY_ON_DAY[lang] || MONTHLY_ON_DAY.nl;
      return fn(dayOfMonth);
    }
    if (pattern && patternLabels[pattern]) return patternLabels[pattern];
    return patternLabels.daily;
  }

  return formatDateNL(activity.date, lang);
}

/**
 * Generate the next N upcoming occurrence dates (YYYY-MM-DD) for a recurring activity.
 * Uses recurring_pattern + recurring_days; falls back to deriving the weekday from
 * activity.date when recurring_days is empty.
 * Returns [] for non-recurring activities (caller should handle that separately).
 */
export function computeUpcomingDates(activity, count = 12) {
  if (!activity) return [];
  const isRecurring = activity.recurring || activity.recurring_pattern || (activity.recurring_days && activity.recurring_days.length > 0);
  if (!isRecurring) return [];

  const pattern = activity.recurring_pattern || 'weekly';
  const baseDate = parseISODateLocal(activity.date);
  if (!baseDate) return [];

  // Normalise recurring_days to canonical NL short codes & convert to JS weekday indices (0=Mon..6=Sun)
  const dayMap = { ma: 0, di: 1, wo: 2, do: 3, vr: 4, za: 5, zo: 6 };
  const normalisedDays = (activity.recurring_days || []).map(normaliseDay).filter((d) => d in dayMap);
  let targetWeekdays = normalisedDays.map((d) => dayMap[d]);

  // Pattern-specific defaults when no days are picked.
  if (targetWeekdays.length === 0) {
    if (pattern === 'daily') {
      targetWeekdays = [0, 1, 2, 3, 4, 5, 6];
    } else if (pattern === 'weekdays') {
      targetWeekdays = [0, 1, 2, 3, 4];
    } else if (pattern === 'weekly' || pattern === 'biweekly') {
      // Use weekday of the base date.
      const baseJs = baseDate.getDay(); // 0=Sun..6=Sat
      const monBased = (baseJs + 6) % 7; // 0=Mon..6=Sun
      targetWeekdays = [monBased];
    }
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = baseDate > today ? baseDate : today;

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Schoolvakanties overslaan wanneer organisator dat heeft aangegeven.
  // We genereren extra kandidaten zodat we na het filteren nog het gevraagde aantal overhouden.
  const skipHolidays = activity.continues_during_holidays === false;
  const collectCount = skipHolidays ? Math.max(count * 2, count + 30) : count;
  const finalize = (arr) => {
    const filtered = skipHolidays ? arr.filter((d) => !isSchoolHoliday(d)) : arr;
    return filtered.slice(0, count);
  };

  const results = [];

  if (pattern === 'monthly_weekday'
      && activity.recurring_week_ordinal != null
      && activity.recurring_weekday != null) {
    // Nde weekdag van de maand. JS Date: 0=Sun..6=Sat. We slaan op als
    // 0=Mon..6=Sun, dus converteren naar JS-weekday.
    const monBased = activity.recurring_weekday; // 0=Mon..6=Sun
    const jsWeekday = (monBased + 1) % 7;
    const ord = activity.recurring_week_ordinal;
    let y = start.getFullYear();
    let m = start.getMonth();
    for (let i = 0; i < 36 && results.length < collectCount; i++) {
      let occurrence;
      if (ord === -1) {
        const lastDay = new Date(y, m + 1, 0);
        occurrence = new Date(lastDay);
        while (occurrence.getDay() !== jsWeekday) occurrence.setDate(occurrence.getDate() - 1);
      } else {
        const first = new Date(y, m, 1);
        while (first.getDay() !== jsWeekday) first.setDate(first.getDate() + 1);
        first.setDate(first.getDate() + (ord - 1) * 7);
        occurrence = first.getMonth() === m ? first : null;
      }
      if (occurrence && occurrence >= today) results.push(fmt(occurrence));
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
    return finalize(results);
  }

  if (pattern === 'monthly') {
    // Every month on the same day-of-month as base date, starting from today.
    const dayOfMonth = baseDate.getDate();
    let y = today.getFullYear();
    let m = today.getMonth();
    for (let i = 0; i < 36 && results.length < collectCount; i++) {
      const lastDay = new Date(y, m + 1, 0).getDate();
      const day = Math.min(dayOfMonth, lastDay);
      const candidate = new Date(y, m, day);
      if (candidate >= today) results.push(fmt(candidate));
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
    return finalize(results);
  }

  if (pattern === 'biweekly') {
    // Increment by 14 days from base date until we have N occurrences in the future.
    let cursor = new Date(baseDate);
    // Advance to first occurrence >= today
    while (cursor < today) cursor.setDate(cursor.getDate() + 14);
    for (let i = 0; i < collectCount; i++) {
      results.push(fmt(cursor));
      cursor = new Date(cursor);
      cursor.setDate(cursor.getDate() + 14);
    }
    return finalize(results);
  }

  // daily / weekdays / weekly with explicit days → walk day by day, max ~120 days ahead
  const cursor = new Date(start);
  for (let i = 0; i < 365 && results.length < collectCount; i++) {
    const monBased = (cursor.getDay() + 6) % 7;
    if (targetWeekdays.includes(monBased)) {
      results.push(fmt(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return finalize(results);
}

/**
 * Format a datetime ISO string to "20 maart 2026 om 14:00"
 */
export function formatDateTimeNL(isoStr) {
  if (!isoStr) return '-';
  try {
    const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 
                    'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
    const d = new Date(isoStr);
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} om ${hours}:${minutes}`;
  } catch {
    return isoStr;
  }
}
